import {
  BreakpointsPx,
  colors,
  ImpFlex,
  ImpHeader,
  ImpNavIcon,
  ImpText,
  zIndex,
} from '@imprint-payments/imprint-ui';
import { useEffect, useState } from 'react';

import { getVirtualCardUrl } from '../../api/api';
import { getLockupPngUrl } from '../../api/getLockupImage/getLockupImage';
import { useImpState } from '../../app/hooks';
import {
  emitSDKEvent,
  emitSDKResult,
  SDK_EVENTS,
  SDK_RESULTS,
  sendLogoUrlToMobileApp,
  showSDKHeader,
  useSDKContext,
} from '../../utils/sdk';
import { RewardsTrigger } from '../AlreadyAppliedText/AlreadyAppliedText';

interface SDKHeaderProps {
  isPreSubmit?: boolean;
  backgroundColor?: string;
}

export default function SDKHeader({
  isPreSubmit = false,
  backgroundColor,
}: SDKHeaderProps) {
  const { merchantData } = useImpState();
  const [width, setWidth] = useState(window.innerWidth);
  const { result, errorCode } = useSDKContext();
  const breakpoint = BreakpointsPx.Application;

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    sendLogoUrlToMobileApp(getLogoUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCloseClick = () => {
    emitSDKResult({
      result,
      ...(result === SDK_RESULTS.ERROR && { errorCode }),
    });
    emitSDKEvent({
      event: SDK_EVENTS.CLOSED,
    });
  };

  const isDesktopLayout = () => {
    return width >= BreakpointsPx.Application;
  };

  const showRewardsHeader = () => {
    return isPreSubmit && !isDesktopLayout();
  };

  const getLogoUrl = () => {
    return showRewardsHeader()
      ? getVirtualCardUrl(merchantData.productUUID)
      : getLockupPngUrl(merchantData.productUUID);
  };

  const renderCardThumbnail = () => {
    const src = merchantData.cardImg || getLogoUrl();
    return (
      src && (
        <img
          src={src}
          alt="card-img"
          style={{
            height: '32px',
            width: 'auto',
            boxShadow: '0px 2px 8px -2px rgba(0, 0, 0, 0.25)',
            borderRadius: '4px',
          }}
        />
      )
    );
  };

  const renderLockupImage = () => {
    const src = merchantData.lockupImage || getLogoUrl();
    return src && <img src={src} alt="lockup-img" height="24px" />;
  };

  const renderNavBar = () => {
    return (
      <div
        style={{
          width: '100%',
          height: '56px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: isDesktopLayout()
            ? colors.surface.surfaceHigh
            : backgroundColor,
          borderBottom: isDesktopLayout()
            ? `1px solid ${colors.border.borderVariant}`
            : 'none',
        }}
      >
        {showRewardsHeader() ? renderCardThumbnail() : renderLockupImage()}
        <div
          style={{
            position: 'absolute',
            right: '8px',
            top: '0',
          }}
        >
          <ImpNavIcon.Close onClick={onCloseClick} />
        </div>
      </div>
    );
  };

  const renderRewardsHeader = () => {
    return (
      <ImpHeader
        leadingContent={
          <ImpText typography="body3">
            {merchantData.rewardDetails.cardName}
          </ImpText>
        }
        trailingContent={<RewardsTrigger breakpoint={breakpoint} />}
      />
    );
  };

  return (
    <div
      style={{
        position: 'sticky',
        top: '0',
        width: '100%',
        zIndex: zIndex.nav,
      }}
    >
      <ImpFlex direction="column">
        {showSDKHeader() && renderNavBar()}
        {showRewardsHeader() && renderRewardsHeader()}
      </ImpFlex>
    </div>
  );
}
